/*
 * Copyright 2026 Sony Corporation
 */

import { Box, CircularProgress, CssBaseline, ThemeProvider } from '@mui/material';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect, type ReactElement } from 'react';

import type { AppConfig } from './config';
import { AuthProvider } from './contexts/AuthProvider';
import { theme } from './theme';
import { useAuth } from './contexts/auth-context';
import { Login } from './pages/Login';
import { loadScenePage, loadWorldEditor } from './pages/loaders';
import { WorldList } from './pages/WorldList';

const ScenePage = lazy(() => loadScenePage().then((module) => ({ default: module.ScenePage })));
const WorldEditor = lazy(() => loadWorldEditor().then((module) => ({ default: module.WorldEditor })));

const RequireAuth = ({ children }: { children: ReactElement }) => {
  const { signedIn } = useAuth();
  const location = useLocation();

  // Once signed in, both 3D pages are fetched when the browser is idle, so opening one does not wait on it.
  useEffect(() => {
    if (!signedIn) {
      return;
    }
    const preload = () => {
      void loadWorldEditor();
      void loadScenePage();
    };
    if (typeof requestIdleCallback === 'undefined') {
      const timer = setTimeout(preload, 1000);
      return () => clearTimeout(timer);
    }
    const idle = requestIdleCallback(preload, { timeout: 3000 });
    return () => cancelIdleCallback(idle);
  }, [signedIn]);

  return signedIn ? (
    <Suspense
      fallback={
        <Box className="flex h-screen items-center justify-center">
          <CircularProgress />
        </Box>
      }
    >
      {children}
    </Suspense>
  ) : (
    <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  );
};

export interface AppProps {
  config: AppConfig;
}

export const App = ({ config }: AppProps) => (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <AuthProvider config={config}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/worlds"
            element={
              <RequireAuth>
                <WorldList />
              </RequireAuth>
            }
          />
          <Route
            path="/worlds/:world_id"
            element={
              <RequireAuth>
                <WorldEditor />
              </RequireAuth>
            }
          />
          <Route
            path="/worlds/:world_id/scenes/:scene_id/edit"
            element={
              <RequireAuth>
                <ScenePage />
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/worlds" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  </ThemeProvider>
);
